import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../CompanyContext';
import { ordersAPI } from '../../services/api';
import { Order, OrderStatus } from '../../types';
import { useUser } from '../UserContext';
import { toastSuccess, toastError } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCartIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const OrdersList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedCompany } = useCompany();
  const { currentUser, isAdmin } = useUser();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (selectedCompany) {
      fetchOrders();
    }
  }, [selectedCompany, statusFilter]);

  const fetchOrders = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setError(null);
      let data: Order[];
      
      if (statusFilter === 'all') {
        data = await ordersAPI.getByCompany(selectedCompany.id);
      } else {
        data = await ordersAPI.getByStatus(statusFilter as OrderStatus);
        // Filter by company as well since the API might not support both filters
        data = data.filter(order => order.companyId === selectedCompany.id);
      }
      
      setOrders(data);
    } catch (err) {
      setError(t('orders.errorTitle'));
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const canDelete = (currentUser?.role === 'owner') || !!isAdmin;

  const handleDelete = async (orderId: string) => {
    if (!canDelete) return;
    const confirmed = window.confirm(t('orders.actions.confirmDelete'));
    if (!confirmed) return;
    try {
      await ordersAPI.delete(orderId);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toastSuccess(t('orders.actions.deleted'));
    } catch (e) {
      toastError(t('orders.actions.deleteFailed'));
      console.error('Delete order error:', e);
    }
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.ASSIGNED:
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case OrderStatus.IN_PROGRESS:
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case OrderStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case OrderStatus.CANCELLED:
      case OrderStatus.EXPIRED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case OrderStatus.ASSIGNED:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case OrderStatus.IN_PROGRESS:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case OrderStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case OrderStatus.CANCELLED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case OrderStatus.EXPIRED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language || 'en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('orders.noCompanyTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('orders.noCompanyText')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('orders.errorTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchOrders}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          {t('orders.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('orders.title')}</h1>
          <p className="text-gray-600">{t('orders.manageFor', { company: selectedCompany.name })}</p>
        </div>
        <Link
          to="/orders/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('orders.new')}
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">{t('orders.filterLabel')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">{t('orders.filterAll')}</option>
            <option value={OrderStatus.ASSIGNED}>{t('orders.statuses.ASSIGNED')}</option>
            <option value={OrderStatus.IN_PROGRESS}>{t('orders.statuses.IN_PROGRESS')}</option>
            <option value={OrderStatus.COMPLETED}>{t('orders.statuses.COMPLETED')}</option>
            <option value={OrderStatus.CANCELLED}>{t('orders.statuses.CANCELLED')}</option>
            <option value={OrderStatus.EXPIRED}>{t('orders.statuses.EXPIRED')}</option>
          </select>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('orders.emptyTitle')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter === 'all' 
                ? t('orders.emptyAll')
                : t('orders.emptyFiltered', { status: t(`orders.statuses.${statusFilter}`) })
              }
            </p>
            {statusFilter === 'all' && (
              <div className="mt-6">
                <Link
                  to="/orders/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('orders.emptyCta')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.order')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.dueDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.assignedTo')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('orders.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(order.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            #{order.id.slice(0, 8)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(order.assignedDate)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.customerName || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{order.customerEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(order.status)}>
                        {t(`orders.statuses.${order.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.dueDate ? formatDate(order.dueDate) : t('products.na')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.assignedToUser ? 
                        `${order.assignedToUser.firstName} ${order.assignedToUser.lastName}` : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('orders.actions.view')}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/orders/${order.id}/edit`}
                          className="text-gray-600 hover:text-gray-900"
                          title={t('orders.actions.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                        {order.status === OrderStatus.COMPLETED && (
                          <Link
                            to={`/sales/new?orderId=${order.id}`}
                            className="text-green-600 hover:text-green-900"
                            title={t('orders.actions.generateSale')}
                          >
                            <CurrencyDollarIcon className="h-4 w-4" />
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('orders.actions.delete')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;
